import { gql } from '@apollo/client';
import { Column } from '@tanstack/react-table';

import { prepareFilters } from '@work4all/components';

import {
  ParsedCustomFieldConfig,
  prepareCustomFieldsDefinitions,
} from '@work4all/data/lib/custom-fields';
import { transformFilter } from '@work4all/data/lib/hooks/data-provider/utils/buildQuery';

import { entityDefinition } from '@work4all/models/lib/Classes/entityDefinitions';
import { SavedListFilter } from '@work4all/models/lib/Classes/SavedListFilter.entity';
import { FieldDefinitions, Filters } from '@work4all/models/lib/DataProvider';
import { Entities } from '@work4all/models/lib/Enums/Entities.enum';

import { deserializeFilter } from '../../../../utils/deserializeFilter';
import {
  parseTableSettings,
  TableSettingFilter,
} from '../../../data-tables/make-table-settings';

import { parseFilterConfig } from './parseFilterConfig';

type GetSavedFilterRequestProps = {
  entity: Entities;
  columns: Column<unknown>[];
  savedFilters: SavedListFilter[];
  customFields: ParsedCustomFieldConfig[];
};

export const getSavedFiltersCountsQuery = ({
  entity,
  columns,
  savedFilters,
  customFields,
}: GetSavedFilterRequestProps) => {
  const requests = [];
  const definition = entityDefinition[entity];

  if (!definition) {
    return null;
  }

  const queryName = definition?.remote.queryName;

  const customFieldsDefinitions = prepareCustomFieldsDefinitions(customFields);
  const fields = {
    ...definition.fieldDefinitions,
    ...customFieldsDefinitions,
  };

  savedFilters.forEach((savedFilter) => {
    const { filter, extraParams } = getRequestConfig({
      entity,
      columns,
      savedFilter,
      fields,
    });

    const normalizedSavedFilterId = savedFilter.id.split('-').join('');
    const requestId = `request_${normalizedSavedFilterId}`;

    const preparedQueryParams = prepareQueryParams({ filter, ...extraParams });

    requests.push(`${requestId}: ${queryName}${preparedQueryParams}
     {
      total
    }`);
  });

  return gql`query GetSavedFiltersCounts {${requests.join('\n')}}`;
};

type GetRequestConfigProps = {
  entity: Entities;
  columns: Column<unknown>[];
  savedFilter: SavedListFilter;
  fields: FieldDefinitions<unknown>;
};

const getRequestConfig = ({
  columns,
  savedFilter,
  fields,
  entity,
}: GetRequestConfigProps) => {
  const parsedFilter = parseFilterConfig(savedFilter.filter);

  const settingsFilter: TableSettingFilter[] =
    parseTableSettings(parsedFilter.filter)?.filter ?? [];

  const validSettingsFilters = getValidSettingsFilters({
    columns,
    filters: settingsFilter,
  });

  const normalizedSettingsFilters = normalizeSettingFilters({
    columns,
    filters: validSettingsFilters,
  });

  const { filter: preparedSettingsFilters } = prepareFilters(
    normalizedSettingsFilters,
    undefined,
    undefined,
    entity
  );

  const filter = getRequestFilter({
    entity,
    fields,
    parsedFilter: parsedFilter.prefilter ?? [],
    preparedSettingsFilters: preparedSettingsFilters ?? [],
  });

  const extraParams = getRequestExtraParams({
    entity,
    prefilter: parsedFilter.prefilter,
  });

  return {
    filter,
    extraParams,
  };
};

type GetValidSettingsFiltersProps = {
  columns: Column<unknown>[];
  filters: TableSettingFilter[];
};

const getValidSettingsFilters = ({
  columns,
  filters,
}: GetValidSettingsFiltersProps) => {
  return filters.filter((filter) => {
    const column = columns.find((column) => column.id === filter.id);
    return column && column?.columnDef.filterType;
  });
};

type NormalizeSettingFiltersProps = {
  filters: TableSettingFilter[];
  columns: Column<unknown>[];
};

const normalizeSettingFilters = ({
  filters,
  columns,
}: NormalizeSettingFiltersProps) => {
  return filters.map((filter) => {
    const column = columns.find((column) => column.id === filter.id);

    const deserializedFilter = deserializeFilter(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filter as any,
      column.columnDef.filterType
    );

    return {
      id:
        !column?.columnDef.filterField ||
        column.columnDef.filterField.length === 0
          ? filter.id
          : column.columnDef.filterField,
      value: {
        ...deserializedFilter,
        filterType: column.columnDef.filterType,
      },
    };
  });
};

type GetRequestFilterProps = {
  preparedSettingsFilters: Filters;
  parsedFilter: Filters;
  entity: Entities;
  fields: FieldDefinitions<unknown>;
};

const getRequestFilter = ({
  entity,
  preparedSettingsFilters,
  parsedFilter,
  fields,
}: GetRequestFilterProps) => {
  if (preparedSettingsFilters.length === 0 && parsedFilter.length === 0) {
    return null;
  }

  const combinedFilters = [...preparedSettingsFilters, ...parsedFilter];

  const transformedFilters = transformFilter({
    filter: combinedFilters,
    fields,
    entity,
  });

  const stringifiedFilters = JSON.stringify(transformedFilters).replace(
    /"/g,
    '\\"'
  );

  return `"${stringifiedFilters}"`;
};

type GetRequestExtraParamsProps = {
  entity: Entities;
  prefilter: Filters;
};

const getRequestExtraParams = ({
  entity,
  prefilter,
}: GetRequestExtraParamsProps) => {
  const result: Record<string, unknown> = {};

  if (!prefilter || prefilter.length === 0) {
    return null;
  }

  if (entity === Entities.positionDetails) {
    const bzObjTypeFilter = prefilter.find((filter) =>
      Object.keys(filter).some((key) => key === 'pos_RealBzObjType')
    );

    if (bzObjTypeFilter) {
      result.bzObjType = bzObjTypeFilter['pos_RealBzObjType']['$eq'];
    }
  }

  if (Object.keys(result).length === 0) {
    return null;
  }

  return result;
};

const prepareQueryParams = (params: Record<string, unknown>) => {
  let stringifiedParams = '';
  const paramsValues = Object.values(params);
  const filteredParamsValues = paramsValues.filter(Boolean);

  if (filteredParamsValues.length === 0) {
    return '';
  }

  for (const [key, value] of Object.entries(params)) {
    stringifiedParams += `${key}: ${value}\n`;
  }

  return `(${stringifiedParams})`;
};
